import React, {useId, useLayoutEffect, useRef, useState} from "react";
import {VisuallyHidden} from "components/Accessibility.styles";
import formatBytes, {formatRate} from "core/format/byteFormat";
import {type RecipientActivity, RecipientState} from "core/p2p/peerActivity";
import {useLocale, useMessages} from "i18n";
import {orderedRecipients, recipientRows, recipientsPace} from "./liveRecipientRules";
import {RecipientsRing} from "./RecipientsRing";
import {
	Done, Empty, EmptyHint, EmptyTitle, Eyebrow, Figures, FoldLabel, FoldRow, FoldToggle, LIST_PEEK_PX, List, Mark,
	Name, Row, Section, Tick, WaitDot,
} from "./LiveRecipients.styles";

/** Rows shown before the list scrolls. */
const VISIBLE_ROWS = 2;

/**
 * Holds `list` to its first {@link VISIBLE_ROWS} rows plus a look at the next, measured rather than fixed because
 * a row's figures wrap in a long locale. The ROWS are observed, not the list: once capped, the list's own box no
 * longer changes when a row grows (a font arriving, a row's figures wrapping), so watching it would leave a stale
 * cap. `rowsKey` changes when the rows on show or their order change, which re-measures too.
 */
function useTwoRowHeight(list: React.RefObject<HTMLUListElement | null>, scrolls: boolean, rowsKey: string): void {
	useLayoutEffect(() => {
		const element = list.current;
		if (element === null) {
			return;
		}
		if (!scrolls) {
			element.style.maxHeight = "";
			return;
		}
		const measure = () => {
			const rows = element.children;
			const first = rows[0] as HTMLElement | undefined;
			const next = rows[VISIBLE_ROWS] as HTMLElement | undefined;
			if (first !== undefined && next !== undefined) {
				element.style.maxHeight = `${next.offsetTop - first.offsetTop + LIST_PEEK_PX}px`;
			}
		};
		measure();
		const observer = new ResizeObserver(measure);
		for (const row of element.children) {
			observer.observe(row);
		}
		return () => observer.disconnect();
	}, [list, scrolls, rowsKey]);
}

function FinishedTick() {
	return (
		<Tick viewBox="0 0 16 16" aria-hidden="true">
			<path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" strokeWidth="2.2"
				strokeLinecap="round" strokeLinejoin="round"/>
		</Tick>
	);
}

function RecipientRow({recipient}: {recipient: RecipientActivity}) {
	const {sender, peerConnection} = useMessages();
	const {locale} = useLocale();
	const size = (bytes: number) => formatBytes(bytes, locale, 2, 0);
	const {instant} = sender;
	return (
		<Row>
			{recipient.state === RecipientState.Finished
				? <FinishedTick/>
				: <Mark $downloading={recipient.state === RecipientState.Downloading} aria-hidden="true"/>}
			<Name>{instant.recipient(recipient.number)}</Name>
			<Figures>
				{recipient.state === RecipientState.Downloading && <>
					<span>{sender.progress.sentOfTotal(size(recipient.sentBytes), size(recipient.requestedBytes))}</span>
					{recipient.bytesPerSecond !== null
						&& <strong>{peerConnection.speed(formatRate(recipient.bytesPerSecond, locale))}</strong>}
				</>}
				{recipient.state === RecipientState.Opened && <span>{instant.opened}</span>}
				{recipient.state === RecipientState.Finished && <>
					<Done>{instant.done}</Done>
					<span>{instant.downloaded(size(recipient.sentBytes))}</span>
				</>}
			</Figures>
		</Row>
	);
}

/**
 * Who is downloading (kitecorp/transferify-ui#3320): the ring over everyone, then a row per recipient in the order
 * `./liveRecipientRules` sets, finished rows folding away from five, two rows on show and the rest scrolling inside
 * the list. Nobody yet: the waiting line and who will appear here. Every rule is in `./liveRecipientRules`; this only
 * renders what it returns.
 */
export function LiveRecipients({recipients}: {recipients: readonly RecipientActivity[]}) {
	const {sender} = useMessages();
	const {instant} = sender;
	const headingId = useId();
	const foldLabelId = useId();
	const listRef = useRef<HTMLUListElement>(null);
	const [expanded, setExpanded] = useState(false);
	const ordered = orderedRecipients(recipients);
	const {rows, fold} = recipientRows(ordered, expanded);
	const scrolls = rows.length + (fold === null ? 0 : 1) > VISIBLE_ROWS;
	useTwoRowHeight(listRef, scrolls, rows.map(recipient => `${recipient.number}:${recipient.state}`).join());
	const downloading = recipientsPace(ordered).downloading;
	return (
		<Section aria-labelledby={headingId}>
			<Eyebrow id={headingId}>{instant.recipients}</Eyebrow>
			{ordered.length === 0 ? (
				<Empty>
					<WaitDot aria-hidden="true"/>
					<div>
						<EmptyTitle>{instant.waiting}</EmptyTitle>
						<EmptyHint>{instant.waitingHint}</EmptyHint>
					</div>
				</Empty>
			) : <>
				<RecipientsRing ordered={ordered}/>
				{/* The count the old title announced, said again only when it changes, never per speed reading, and
				    nothing while nobody is downloading. */}
				<VisuallyHidden role="status" aria-live="polite">
					{downloading > 0 ? instant.downloadingNow(downloading) : null}
				</VisuallyHidden>
				{/* An explicit role: WebKit drops a list's role once its markers are styled away. */}
				<List ref={listRef} role="list" $scrolls={scrolls} aria-labelledby={headingId}
					tabIndex={scrolls ? 0 : undefined}>
					{rows.map(recipient => <RecipientRow key={recipient.number} recipient={recipient}/>)}
					{fold !== null && (
						<FoldRow>
							<FinishedTick/>
							<FoldLabel id={foldLabelId}>{instant.finishedFold(fold.finished)}</FoldLabel>
							<FoldToggle type="button" aria-expanded={expanded} aria-describedby={foldLabelId}
								onClick={() => setExpanded(open => !open)}>
								{expanded ? instant.hideFinished : instant.showFinished}
							</FoldToggle>
						</FoldRow>
					)}
				</List>
			</>}
		</Section>
	);
}
